import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ImagePlus, X, Loader2, CalendarCheck, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/context/Lang";
import { SERVICE_OPTIONS } from "@/data";
import { Reveal, Overline } from "@/components/site/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TIMES = ["08:00", "10:00", "12:00", "14:00", "16:00", "18:00"];
const today = new Date().toISOString().split("T")[0];

export default function LeadForm() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", zip: "", service: "", photo_url: "" });
  const [booking, setBooking] = useState({ date: "", time: "" });
  const [step, setStep] = useState(1); // 1 = quote, 2 = schedule, 3 = done
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const setBk = (k) => (e) => setBooking((b) => ({ ...b, [k]: e.target ? e.target.value : e }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd);
      setForm((f) => ({ ...f, photo_url: `${BACKEND}${data.url}` }));
    } catch {
      toast.error(t.form.photoError);
    } finally {
      setUploading(false);
    }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!form.phone.trim()) { toast.error(t.form.phoneReq); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        photo_url: form.photo_url || undefined,
        source: "form",
      });
      toast.success(t.form.success);
      setStep(2);
    } catch {
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!booking.date || !booking.time) { toast.error(t.book.selectTime); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name, phone: form.phone,
        service: form.service, date: booking.date, time: booking.time,
      });
      toast.success(t.book.success);
      setStep(3);
    } catch {
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="bg-white py-24 md:py-32" data-testid="lead-form">
      <div className="max-w-6xl mx-auto px-4 md:px-8 grid lg:grid-cols-[0.85fr_1fr] gap-12 lg:gap-16 items-center">
        <Reveal>
          <Overline>{t.form.overline}</Overline>
          <h2 className="mt-4 font-display font-extrabold text-navy text-4xl md:text-6xl leading-[0.95] tracking-tighter">
            {t.form.title}
          </h2>
          <div className="mt-6 inline-flex items-center gap-2 bg-gold/15 text-navy font-bold text-sm px-4 py-2.5 rounded-full border border-gold/40">
            {t.form.urgency}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          {step === 1 && (
            <form onSubmit={submitQuote} className="bg-[#F8F9FA] border border-navy/10 p-6 md:p-8 space-y-4" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="lead-name" placeholder={t.form.name} value={form.name} onChange={set("name")} className="h-12 bg-white rounded-none border-navy/15" />
                <Input data-testid="lead-phone" placeholder={t.form.phone} value={form.phone} onChange={set("phone")} className="h-12 bg-white rounded-none border-navy/15" required />
              </div>
              <Input data-testid="lead-zip" placeholder={t.form.zip} value={form.zip} onChange={set("zip")} className="h-12 bg-white rounded-none border-navy/15" />
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger data-testid="lead-service" className="h-12 bg-white rounded-none border-navy/15">
                  <SelectValue placeholder={t.form.selectService} />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`lead-service-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="lead-photo-input" />
              {form.photo_url ? (
                <div className="relative border border-navy/15 bg-white" data-testid="lead-photo-preview">
                  <img src={form.photo_url} alt="Photo of your space uploaded for a free quote" className="w-full h-40 object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photo_url: "" }))} data-testid="lead-photo-remove" className="absolute top-2 right-2 bg-navy/80 text-white p-1.5 hover:bg-navy transition-colors">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="lead-photo-btn" className="w-full flex items-center justify-center gap-2 border border-dashed border-navy/25 bg-white text-navy/60 hover:border-orange hover:text-orange py-3.5 text-sm font-semibold transition-colors">
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                  {uploading ? t.form.uploading : t.form.photo}
                </button>
              )}

              <button type="submit" data-testid="lead-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-[#1550C0] disabled:opacity-60 text-white font-bold text-base px-8 py-4 rounded-full transition-colors">
                {loading ? t.form.sending : t.form.submit}
                {!loading && <ArrowRight size={18} />}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitBooking} className="bg-[#F8F9FA] border border-navy/10 p-6 md:p-8 space-y-5" data-testid="schedule-step">
              <div className="flex items-center gap-2 text-orange font-bold text-sm">
                <CheckCircle2 size={18} /> {t.form.success}
              </div>
              <div>
                <h3 className="font-display font-extrabold text-navy text-2xl leading-tight">{t.book.step2Title}</h3>
                <p className="mt-1 text-navy/60 text-sm">{t.book.step2Sub}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="book-date" type="date" min={today} value={booking.date} onChange={setBk("date")} className="h-12 bg-white rounded-none border-navy/15" required />
                <Select value={booking.time} onValueChange={setBk("time")}>
                  <SelectTrigger data-testid="book-time" className="h-12 bg-white rounded-none border-navy/15 text-navy">
                    <SelectValue placeholder={t.book.selectTime} />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMES.map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button type="submit" data-testid="book-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-[#1550C0] disabled:opacity-60 text-white font-bold text-base px-8 py-4 rounded-full transition-colors">
                <CalendarCheck size={18} />
                {loading ? t.book.sending : t.book.submit}
              </button>
              <button type="button" onClick={() => setStep(3)} data-testid="book-skip" className="w-full text-navy/50 hover:text-navy text-sm font-semibold transition-colors">
                {t.book.skip}
              </button>
            </form>
          )}

          {step === 3 && (
            <div className="bg-[#F8F9FA] border border-navy/10 p-8 md:p-12 text-center" data-testid="quote-done">
              <div className="mx-auto w-16 h-16 rounded-full bg-orange/10 flex items-center justify-center">
                <CheckCircle2 size={34} className="text-orange" />
              </div>
              <h3 className="mt-6 font-display font-extrabold text-navy text-3xl leading-tight">{t.book.thanksTitle}</h3>
              <p className="mt-3 text-navy/60">{t.book.thanksSub}</p>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}
